import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { AppCard, AppInput, AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import Translator from '@/layout/components/helpers/Translator';
import * as currentPageAction from '@/store/actions/currentPageAction';
import { axiosPath, path } from '@/routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import BreadcrumbHandler from '@/layout/components/breadcrumb/BreadcrumbHandler';
import DevelopmentCard from '@/layout/screenSpecifics/Modules/DynamicForm/DevelopmentCard';

// import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
// import useIsMounted from '../../../layout/components/hooks/useIsMounted';
// import { errorHandler } from '../../../layout/components/error/errorHandlerNew';
// import { axiosPath, path } from '../../../routing/helpers/router';
// import Translator from '../../../layout/components/helpers/Translator';
// import DevelopmentCard from '../../../layout/screenSpecifics/Modules/DynamicForm/DevelopmentCard';

export default function ModuleDynamicFormEndConfirmScreen() {
  const { moduleBridgeHex, dynamicFormUserFormPageID } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState();
  const [modalWarning, setModalWarning] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      formName: Yup.string().required(
        <Translator file="user" name="user.required" />,
      ),
    }),
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD FORM DATA
  const loadFormData = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_dynamicForm_endConfirm_get'), {
          moduleBridgeHex,
          dynamicFormUserFormPageID,
        })
        .then((r) => {
          if (isMounted.current) {
            if (r.status === 203) {
              setModalWarning(r?.data);
              return;
            }
            if (r?.data?.step) {
              navigate(
                path({
                  name: 'user_module_dynamicForm_step',
                  params: {
                    moduleBridgeHex,
                    dynamicFormUserFormPageID: r?.data?.dynamicFormUserFormPageID,
                  },
                }),
              );
            } else {
              setFormData(r?.data);
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, dynamicFormUserFormPageID],
  );

  useEffect(() => {
    loadFormData();
  }, []);
  // < LOAD FORM DATA

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            values?.back
              ? 'api_r_user_module_dynamicForm_back'
              : 'api_r_user_module_dynamicForm_endConfirm_edit',
          ),
          {
            moduleBridgeHex,
            dynamicFormUserFormPageID,
            ...values,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            if (r?.data?.end) {
              navigate(
                path({
                  name: 'user_module_dynamicForm_end',
                  params: {
                    moduleBridgeHex,
                    dynamicFormUserFormID: r?.data?.dynamicFormUserFormID,
                  },
                }),
              );
            } else if (r?.data?.step) {
              navigate(
                path({
                  name: 'user_module_dynamicForm_step',
                  params: {
                    moduleBridgeHex,
                    dynamicFormUserFormPageID: r?.data?.dynamicFormUserFormPageID,
                  },
                }),
              );
            } else if (r.status === 203) {
              setModalWarning(r?.data);
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, dynamicFormUserFormPageID],
  );
  // < SAVE

  return (
    <>
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_dynamicForm_home',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <>
              {formData && (
                <>
                  <AppCard>
                    <Formik
                      initialValues={{
                        formName: '',
                      }}
                      validationSchema={validationSchema}
                      validateOnChange={false}
                      validateOnBlur={false}
                      onSubmit={(v) => handleSave(v)}
                    >
                      {({
                        setFieldValue,
                        handleSubmit,
                        values,
                        errors,
                        isSubmitting,
                      }) => (
                        <form className="page-form" id="step" onSubmit={handleSubmit}>
                          <div className="container">
                            <div className="row">
                              <div className="col-lg-12">
                                <h3>Formulario terminado</h3>
                                <p>
                                  Al guardar el formulario, podrá ver el resultado y
                                  ya no podrá ser modificado.
                                </p>
                                <AppInput
                                  title="Guardar como"
                                  onChange={(v) => setFieldValue('formName', v)}
                                  value={values?.formName}
                                  error={errors?.formName}
                                  required
                                />
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-lg-12 d-flex">
                                {formData?.dynamicFormUserFormPageID && (
                                  <button
                                    type="button"
                                    className="btn btn-light"
                                    disabled={isSubmitting}
                                    onClick={async () => {
                                      handleSave({ ...values, back: true });
                                    }}
                                  >
                                    <AppRemixIcon
                                      icon="corner-down-left"
                                      className="mr-2"
                                    />
                                    Volver
                                  </button>
                                )}
                                <button
                                  type="submit"
                                  className="btn btn-success"
                                  disabled={isSubmitting}
                                  style={{ marginLeft: 'auto' }}
                                >
                                  <AppRemixIcon icon="save" /> Guardar
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </Formik>
                  </AppCard>
                  <DevelopmentCard pvGlobalData={formData?.pvGlobalData} />
                </>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}
